// src/pages/CustomersPage.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getOrders } from '../lib/db';

const empty = () => ({ firstName:'', lastName:'', phone:'', email:'', address:'', city:'Yellowknife', stPr:'NT', zipPostal:'', country:'Canada', notes:'' });
const fmt = n => '$' + (n||0).toFixed(2);

export function CustomersPage() {
  const { shopId } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [orders,    setOrders]    = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form,      setForm]      = useState(empty());
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { if (shopId) load(); }, [shopId]);

  async function load() {
    setLoading(true);
    try { setCustomers(await getCustomers(shopId)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (selected && shopId) {
      getOrders(shopId).then(all => setOrders(all.filter(o => o.customerId === selected.id)));
    }
  }, [selected, shopId]);

  const filtered = customers.filter(c => {
    if (!search) return true;
    const t = search.toLowerCase();
    return `${c.firstName} ${c.lastName} ${c.phone} ${c.email}`.toLowerCase().includes(t);
  });

  function openNew() { setForm(empty()); setEditId(null); setShowModal(true); }
  function openEdit(c) { setForm({ ...empty(), ...c }); setEditId(c.id); setShowModal(true); }

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    try {
      if (editId) {
        await updateCustomer(shopId, editId, form);
        setCustomers(cs => cs.map(c => c.id === editId ? { ...c, ...form } : c));
        if (selected?.id === editId) setSelected(s => ({ ...s, ...form }));
      } else {
        const id = await createCustomer(shopId, form);
        const newC = { id, ...form };
        setCustomers(cs => [...cs, newC]);
      }
      setShowModal(false);
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this customer?')) return;
    await deleteCustomer(shopId, id);
    setCustomers(cs => cs.filter(c => c.id !== id));
    if (selected?.id === id) setSelected(null);
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const totalSpent = orders.reduce((s,o) => s + (o.total||0), 0);

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <input className="input" style={{maxWidth:300}} placeholder="Search customers…" value={search} onChange={e=>setSearch(e.target.value)}/>
        <button className="btn btn-primary" onClick={openNew}>+ New Customer</button>
      </div>

      <div style={{display:'flex',gap:16,height:'calc(100vh - 52px - 110px)'}}>
        {/* List */}
        <div style={{flex:1,overflow:'auto'}}>
          {loading ? (
            <div className="loading-center"><div className="spinner"/></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>No customers found</h3>
              <p>{search ? 'Try a different search.' : 'Add your first customer to get started.'}</p>
            </div>
          ) : (
            <div className="card" style={{padding:0,overflow:'hidden'}}>
              <table className="data-table">
                <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>City</th><th></th></tr></thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} style={{cursor:'pointer',background:selected?.id===c.id?'var(--g50)':''}}
                      onClick={()=>setSelected(c)}>
                      <td style={{fontWeight:600}}>{c.firstName} {c.lastName}</td>
                      <td style={{color:'var(--text-mid)'}}>{c.phone||'—'}</td>
                      <td style={{color:'var(--text-mid)',fontSize:12.5}}>{c.email||'—'}</td>
                      <td style={{color:'var(--text-muted)',fontSize:12.5}}>{c.city||'—'}</td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={e=>{e.stopPropagation();openEdit(c);}}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail */}
        {selected && (
          <div style={{width:300,flexShrink:0,overflow:'auto'}}>
            <div className="card">
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                <h2>{selected.firstName} {selected.lastName}</h2>
                <button style={{background:'none',border:'none',fontSize:18,cursor:'pointer',color:'var(--text-muted)'}} onClick={()=>setSelected(null)}>✕</button>
              </div>
              <div style={{fontSize:13,lineHeight:2,color:'var(--text-mid)',marginBottom:14}}>
                {selected.phone && <div>📞 {selected.phone}</div>}
                {selected.email && <div>✉️ {selected.email}</div>}
                {selected.address && <div>📍 {selected.address}, {selected.city}</div>}
                {selected.notes  && <div style={{marginTop:6,fontStyle:'italic',color:'var(--text-muted)'}}>{selected.notes}</div>}
              </div>

              <div style={{display:'flex',gap:8,marginBottom:16}}>
                <div style={{flex:1,textAlign:'center',background:'var(--g50)',borderRadius:'var(--radius-sm)',padding:'10px 6px'}}>
                  <div style={{fontSize:20,fontWeight:800,color:'var(--g700)'}}>{orders.length}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)'}}>Orders</div>
                </div>
                <div style={{flex:1,textAlign:'center',background:'var(--g50)',borderRadius:'var(--radius-sm)',padding:'10px 6px'}}>
                  <div style={{fontSize:16,fontWeight:800,color:'var(--g700)'}}>{fmt(totalSpent)}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)'}}>Total Spent</div>
                </div>
              </div>

              {orders.length > 0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.7px',color:'var(--text-muted)',marginBottom:8}}>Order History</div>
                  {orders.slice(0,5).map(o => (
                    <div key={o.id} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--parch-mid)',fontSize:13}}>
                      <span style={{color:'var(--text-mid)'}}>#{o.orderNumber||'—'}</span>
                      <span style={{fontWeight:600}}>{fmt(o.total)}</span>
                      <span className={`pill ${o.status==='completed'||o.status==='paid'?'pill-green':'pill-gray'}`} style={{fontSize:10}}>{o.status}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-secondary btn-sm" style={{flex:1}} onClick={()=>openEdit(selected)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={()=>handleDelete(selected.id)}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowModal(false)}}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Customer' : 'New Customer'}</h2>
              <button className="modal-close" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group flex1"><label className="field-label">First Name *</label><input className="input" value={form.firstName} onChange={set('firstName')} required/></div>
                <div className="form-group flex1"><label className="field-label">Last Name *</label><input className="input" value={form.lastName} onChange={set('lastName')} required/></div>
              </div>
              <div className="form-row">
                <div className="form-group flex1"><label className="field-label">Phone</label><input className="input" type="tel" value={form.phone} onChange={set('phone')}/></div>
                <div className="form-group flex1"><label className="field-label">Email</label><input className="input" type="email" value={form.email} onChange={set('email')}/></div>
              </div>
              <div className="form-group"><label className="field-label">Address</label><input className="input" value={form.address} onChange={set('address')}/></div>
              <div className="form-row">
                <div className="form-group flex1"><label className="field-label">City</label><input className="input" value={form.city} onChange={set('city')}/></div>
                <div className="form-group w-sm"><label className="field-label">St/Pr</label><input className="input" value={form.stPr} onChange={set('stPr')}/></div>
                <div className="form-group flex1"><label className="field-label">Country</label><select className="input" value={form.country} onChange={set('country')}><option>Canada</option><option>USA</option></select></div>
              </div>
              <div className="form-group"><label className="field-label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={set('notes')}/></div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving…':'Save Customer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
